import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBase } from "../apiBase";
import {
  FaBuilding,
  FaChartLine,
  FaCode,
  FaSignOutAlt,
  FaUsers,
  FaRobot,
  FaArrowUp,
  FaArrowDown,
  FaSpinner,
  FaDatabase,
  FaComments,
  FaGlobe,
  FaChevronRight,
  FaCheckCircle,
  FaExclamationCircle,
  FaHdd,
  FaCopy,
  FaCheck
} from "react-icons/fa";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("analytics");
  const [tenantId, setTenantId] = useState("");
  const [accessError, setAccessError] = useState("");
  const [isAccessing, setIsAccessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [storageLoading, setStorageLoading] = useState(false);
  const [globalTrendData, setGlobalTrendData] = useState([]);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [storageProgress, setStorageProgress] = useState(0);
  const [storageFetching, setStorageFetching] = useState(false);
  const [storageData, setStorageData] = useState({ active: [], inactive: [] });
  const [platformStats, setPlatformStats] = useState({
    total_tenants: 0,
    active_tenants: 0,
    total_bots: 0,
    total_users: 0,
    total_conversations: 0,
    total_messages: 0,
    avg_latency: 0,
    p95_latency: 0,
    total_storage_gb: 0,
    changes: {
      tenants: 0,
      bots: 0,
      users: 0,
      conversations: 0,
      messages: 0,
      latency: 0,
      storage: 0,
    }
  });
  const [tenants, setTenants] = useState([]);
  const [bots, setBots] = useState([]);
  const [botAnalytics, setBotAnalytics] = useState([]);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [view, setView] = useState("overview");
  const [tenantData, setTenantData] = useState(null);
  const [timeRange, setTimeRange] = useState("7d");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [features, setFeatures] = useState({});
  const [tenantFeaturesMap, setTenantFeaturesMap] = useState({});
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [botTrendData, setBotTrendData] = useState([]);
  const [botTrendPeriod, setBotTrendPeriod] = useState("day");
  const [botDateStart, setBotDateStart] = useState("");
  const [botDateEnd, setBotDateEnd] = useState("");
  const [isTrendLoading, setIsTrendLoading] = useState(false);
  const [copiedTenantId, setCopiedTenantId] = useState(null);
  const [savedTenantId, setSavedTenantId] = useState(null);
  const hasFetched = React.useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchPlatformStats = async () => {
      try {
        const res = await fetch(`${getApiBase()}/admin/overview`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed");

        const total = data.total || {};
        const current = data.current || {};
        const previous = data.previous || {};

        const calcChange = (c, p) => {
          if (p === 0 && c > 0) return 100;
          if (p === 0 && c === 0) return 0;
          return ((c - p) / p) * 100;
        };

        setPlatformStats({
          total_tenants: total.total_tenants || 0,
          active_tenants: current.active_tenants || 0,
          total_bots: total.total_bots || 0,
          total_users: total.total_users || 0,
          total_conversations: total.total_conversations || 0,
          total_messages: total.total_messages || 0,
          avg_latency: parseFloat((total.avg_latency || 0).toFixed(2)),
          p95_latency: parseFloat((total.p95_latency || 0).toFixed(2)),
          total_storage_gb: parseFloat((total.total_storage_gb || 0).toFixed(2)),
          changes: {
            tenants: Number(current.total_tenants || 0),
            bots: Number(current.total_bots || 0),
            users: calcChange(current.total_users || 0, previous.total_users || 0),
            conversations: calcChange(current.total_conversations || 0, previous.total_conversations || 0),
            messages: calcChange(current.total_messages || 0, previous.total_messages || 0),
            latency: calcChange(current.avg_latency || 0, previous.avg_latency || 0),
            storage: calcChange(current.total_storage_gb || 0, previous.total_storage_gb || 0),
          }
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlatformStats();
    fetchStorage();
    fetchBotAnalytics(timeRange);
    fetchDailyUsage(timeRange);

    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setBotDateEnd(today.toISOString().split('T')[0]);
    setBotDateStart(thirtyDaysAgo.toISOString().split('T')[0]);
  }, [timeRange]);

  useEffect(() => {
    fetchGlobalTrend();
  }, []);

  const fetchStorage = async () => {
    if (storageFetching) return;
    setStorageFetching(true);
    setStorageProgress(10);
    let progress = 10;

    const interval = setInterval(() => {
      if (progress < 95) {
        progress += 1 + Math.random() * 2;
      } else {
        progress += 0.2;
      }
      if (progress > 99) progress = 99;
      setStorageProgress(Math.floor(progress));
    }, 200);

    try {
      const res = await fetch(`${getApiBase()}/admin/storage-summary`);
      const data = await res.json();
      clearInterval(interval);
      setStorageProgress(100);
      setTimeout(() => {
        setPlatformStats(prev => ({
          ...prev,
          total_storage_gb: data.total_storage_gb
        }));
        setStorageFetching(false);
      }, 300);
    } catch (err) {
      clearInterval(interval);
      setStorageFetching(false);
      console.error("Storage fetch failed:", err);
    }
  };

  const fetchMonthlyOverview = async () => {
    try {
      const res = await fetch(`${getApiBase()}/admin/monthly-overview`);
      const data = await res.json();
      setMonthlyData(Array.isArray(data) ? data : []);
      setView("monthly");
    } catch (err) {
      console.error("Monthly fetch failed:", err);
    }
  };

  const fetchFullStorage = async () => {
    setView("storage");
    if (storageData.active.length > 0 || storageData.inactive.length > 0) {
      return;
    }
    setStorageLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/admin/full-storage?limit=20&offset=0`);
      const data = await res.json();
      const active = [];
      const inactive = [];
      data.forEach(item => {
        if (item.subscription_status === "active") {
          active.push(item);
        } else {
          inactive.push(item);
        }
      });
      setStorageData({ active, inactive });
    } catch (err) {
      console.error("Storage fetch failed:", err);
    } finally {
      setStorageLoading(false);
    }
  };

  const fetchTenants = async (filter = "all") => {
    setTenantFilter(filter);
    try {
      const url = filter === "all"
        ? `${getApiBase()}/admin/tenants`
        : `${getApiBase()}/admin/tenants?status=${filter}`;
      const res = await fetch(url);
      const data = await res.json();
      const tenantList = Array.isArray(data) ? data : data.tenants || [];
      setTenants(tenantList);
      setView("tenants");

      const featureMap = {};
      await Promise.all(
        tenantList.map(async (t) => {
          const res = await fetch(`${getApiBase()}/tenant/${t.tenant_id}/features`);
          const f = await res.json();
          featureMap[t.tenant_id] = f;
        })
      );
      setTenantFeaturesMap(featureMap);
    } catch (err) {
      console.error("Tenants fetch failed:", err);
    }
  };

  const openFeaturePanel = async (tenantId) => {
    setSelectedTenant(tenantId);
    const res = await fetch(`${getApiBase()}/tenant/${tenantId}/features`);
    const data = await res.json();
    setFeatures(data);
  };

  const fetchBots = async () => {
    if (bots.length > 0) {
      setView("bots");
      return;
    }
    const res = await fetch(`${getApiBase()}/admin/bots-details`);
    const data = await res.json();
    setBots(Array.isArray(data) ? data : data.bots || data.data || []);
    setView("bots");
  };

  const fetchDailyUsage = async (range = "7d") => {
    const res = await fetch(`${getApiBase()}/admin/daily-usage?range=${range}`);
    const data = await res.json();
    setDailyUsage(data);
  };

  const fetchBotAnalytics = async (range = "7d") => {
    const res = await fetch(`${getApiBase()}/admin/bot-analytics?range=${range}`);
    const data = await res.json();
    setBotAnalytics(Array.isArray(data) ? data : []);
  };

  const fetchBotTrend = async (botId, period = "day", start = botDateStart, end = botDateEnd) => {
    if (!botId) return;
    setIsTrendLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        ...(start && { start_date: start }),
        ...(end && { end_date: end })
      });
      const res = await fetch(`${getApiBase()}/bot/${botId}/trend?${params.toString()}`);
      const data = await res.json();
      setBotTrendData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Bot trend fetch failed:", err);
      setBotTrendData([]);
    } finally {
      setIsTrendLoading(false);
    }
  };

  const fetchGlobalTrend = async () => {
    setIsGlobalLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/bots/global-trend`);
      const data = await res.json();
      setGlobalTrendData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Global trend fetch failed:", err);
      setGlobalTrendData([]);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
  };

  const handleTenantFilterClick = (filter) => {
    fetchTenants(filter);
  };

  const handleAccess = async (e) => {
    e.preventDefault();
    setAccessError("");
    setIsAccessing(true);
    if (!tenantId.trim()) {
      setAccessError("Tenant ID is required");
      setIsAccessing(false);
      return;
    }
    try {
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/overview`);
      const data = await res.json();
      if (!res.ok) {
        setAccessError(data.detail || "Invalid Tenant ID");
        return;
      }
      localStorage.setItem("tenant_id", tenantId);
      localStorage.setItem("company_name", data.company_name);
      navigate(`/dashboard/${tenantId}`);
      setTenantData(data);
      setActiveTab("analytics");
    } catch {
      setAccessError("Server error");
    } finally {
      setIsAccessing(false);
    }
  };

  const handleBotCardClick = (bot) => {
    if (selectedBotId === bot.bot_id) {
      setSelectedBotId(null);
      setBotTrendData([]);
    } else {
      setSelectedBotId(bot.bot_id);
      fetchBotTrend(bot.bot_id, botTrendPeriod, botDateStart, botDateEnd);
    }
  };

  const handleBotPeriodChange = (newPeriod) => {
    setBotTrendPeriod(newPeriod);
    if (selectedBotId) {
      fetchBotTrend(selectedBotId, newPeriod, botDateStart, botDateEnd);
    }
  };

  const handleBotDateChange = (type, value) => {
    if (type === 'start') setBotDateStart(value);
    if (type === 'end') setBotDateEnd(value);
  };

  useEffect(() => {
    if (selectedBotId && botDateStart && botDateEnd) {
      fetchBotTrend(selectedBotId, botTrendPeriod, botDateStart, botDateEnd);
    }
  }, [botDateStart, botDateEnd, botTrendPeriod]);

  const activePercentage = platformStats.total_tenants > 0
    ? Math.round((platformStats.active_tenants / platformStats.total_tenants) * 100)
    : 0;

  const hasRealData = globalTrendData.some(d => d.messages > 0 || d.conversations > 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0]?.payload;
    return (
      <div className="bg-[#141427]/95 backdrop-blur-xl border border-white/10 rounded-xl px-4 py-3 shadow-2xl min-w-[220px]">
        <p className="text-[11px] text-gray-400 mb-3 font-semibold tracking-wide">{label}</p>
        <div className="flex justify-between items-center text-xs mb-3">
          <div>
            <p className="text-gray-500 text-[10px] uppercase">Messages</p>
            <p className="text-white font-bold text-sm">{data.messages}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-[10px] uppercase">Conversations</p>
            <p className="text-white font-bold text-sm">{data.conversations}</p>
          </div>
        </div>
      </div>
    );
  };

  const handleCopyTenantId = async (tenantId) => {
    await navigator.clipboard.writeText(tenantId);
    setCopiedTenantId(tenantId);
    setTimeout(() => setCopiedTenantId(null), 2000);
  };

  const handleSaveFeatures = async (tenantId) => {
    await fetch(`${getApiBase()}/tenant/${tenantId}/features`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tenantFeaturesMap[tenantId])
    });
    setSavedTenantId(tenantId);
    setTimeout(() => setSavedTenantId(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white font-sans selection:bg-cyan-500/30">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(6, 182, 212, 0.2); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(6, 182, 212, 0.4); }
      `}</style>

      <nav className="bg-[#141427]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <FaGlobe className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">Droog<span className="text-cyan-400">Admin</span></h2>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Analytics</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-1 bg-white/5 p-1.5 rounded-xl border border-white/5">
              <NavItem label="Overview" icon={<FaChartLine />} active={activeTab === "analytics"} onClick={() => { setActiveTab("analytics"); setView("overview"); }} />
              <NavItem label="Tenants" icon={<FaBuilding />} active={activeTab === "admin"} onClick={() => setActiveTab("admin")} />
              <NavItem label="Referral" icon={<FaCode />} active={activeTab === "referral"} onClick={() => setActiveTab("referral")} />
            </div>

            <div className="flex items-center gap-4">
              <div className="h-8 w-[1px] bg-white/10 hidden md:block"></div>
              <button
                onClick={() => { localStorage.removeItem("token"); navigate("/admin"); }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
              >
                <FaSignOutAlt />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === "analytics" && (
          <div className="space-y-6 animate-fade-in">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                <FaSpinner className="animate-spin text-4xl text-cyan-500 mb-4" />
                <p className="text-sm font-bold tracking-wide">Initializing Dashboard...</p>
              </div>
            ) : (
              <>
                {view === "overview" && (
                  <>
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={fetchMonthlyOverview}
                        className="text-xs font-bold px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 transition"
                      >
                        Overall Period
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      <StatCard
                        label="Tenants"
                        value={platformStats.total_tenants}
                        subText={`▲ ${platformStats.changes?.tenants || 0} this month`}
                        icon={<FaBuilding />}
                        onClick={() => handleTenantFilterClick("all")}
                      />
                      <StatCard
                        label="Active Bots"
                        value={platformStats.total_bots}
                        subText={`${platformStats.changes?.bots || 0} this month`}
                        icon={<FaRobot />}
                        onClick={fetchBots}
                      />
                      <StatCard
                        label="Active Users"
                        value={platformStats.total_users.toLocaleString()}
                        trend={platformStats.changes?.users}
                        icon={<FaUsers />}
                      />
                      <StatCard
                        label="Conversations"
                        value={platformStats.total_conversations.toLocaleString()}
                        trend={platformStats.changes?.conversations}
                        icon={<FaComments />}
                      />
                      <StatCard
                        label="Storage"
                        value={storageFetching ? (<span className="text-sm text-cyan-400 font-bold">Loading {storageProgress}%</span>) : (<span>{platformStats.total_storage_gb} GB</span>)}
                        trend={platformStats.changes?.storage}
                        icon={<FaDatabase />}
                        onClick={fetchFullStorage}
                      />
                    </div>

                    <div className="bg-[#1A1A2E] p-6 rounded-2xl border border-white/5 shadow-xl">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-gray-400 text-xs font-bold uppercase tracking-widest">Analytics</h3>
                      </div>
                      <div className="h-[300px] w-full bg-[#0F0F1A] rounded-xl border border-white/5 p-4 relative">
                        {isGlobalLoading ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <FaSpinner className="animate-spin text-2xl text-cyan-500" />
                          </div>
                        ) : hasRealData ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={globalTrendData}>
                              <defs>
                                <linearGradient id="colorConvGlobal" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorMsgGlobal" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                              <Tooltip content={<CustomTooltip />} />
                              <Area type="monotone" dataKey="conversations" stroke="#22d3ee" fill="url(#colorConvGlobal)" />
                              <Area type="monotone" dataKey="messages" stroke="#3b82f6" fill="url(#colorMsgGlobal)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-gray-500 text-sm font-bold">No data available</div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-1 bg-[#1A1A2E] p-6 rounded-2xl border border-white/5 shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
                        <div className="flex justify-between items-center mb-8 relative z-10">
                          <h3 className="text-gray-400 text-xs font-bold uppercase tracking-widest">Tenant Status</h3>
                          <div className="flex items-center gap-3 text-[10px]">
                            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]"></span>Active</span>
                            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-600"></span>Inactive</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center justify-center relative z-10">
                          <div className="relative w-40 h-40">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                              <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2937" strokeWidth="8" />
                              <circle cx="50" cy="50" r="42" fill="none" stroke="url(#gradient1)" strokeWidth="8" strokeDasharray={`${activePercentage * 2.64} 264`} strokeLinecap="round" className="transition-all duration-1000 ease-out" />
                              <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#22d3ee" />
                                  <stop offset="100%" stopColor="#3b82f6" />
                                </linearGradient>
                              </defs>
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-3xl font-bold text-white">{activePercentage}%</span>
                              <span className="text-[10px] text-gray-500 uppercase mt-1 font-bold">Utilization</span>
                            </div>
                          </div>
                          <div className="mt-6 w-full space-y-3">
                            <div onClick={() => handleTenantFilterClick("active")} className="cursor-pointer flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-cyan-500/10 hover:border-cyan-500/30 transition-all group">
                              <span className="text-xs text-gray-400 font-bold group-hover:text-cyan-400">Active Tenants</span>
                              <span className="text-sm font-bold text-cyan-400">{platformStats.active_tenants}</span>
                            </div>
                            <div onClick={() => handleTenantFilterClick("inactive")} className="cursor-pointer flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-red-500/10 hover:border-red-500/30 transition-all group">
                              <span className="text-xs text-gray-400 font-bold group-hover:text-red-400">Inactive Tenants</span>
                              <span className="text-sm font-bold text-gray-400 group-hover:text-red-400">{platformStats.total_tenants - platformStats.active_tenants}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="lg:col-span-1 bg-[#1A1A2E] p-6 rounded-2xl border border-white/5 shadow-xl">
                        <div className="flex justify-between items-start mb-6">
                          <h3 className="text-gray-400 text-xs font-bold uppercase tracking-widest">Key Metrics</h3>
                          <FaChartLine className="text-cyan-500/80 text-lg" />
                        </div>
                        <div className="space-y-4">
                          <div className="p-4 rounded-xl bg-gradient-to-r from-white/[0.02] to-transparent border border-white/5">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs text-gray-400 font-bold">Avg Latency</span>
                              <span className={`text-xs font-bold ${platformStats.avg_latency < 100 ? 'text-green-400' : 'text-yellow-400'}`}>{platformStats.avg_latency}ms</span>
                            </div>
                            <div className="w-full bg-[#0F0F1A] h-1.5 rounded-full overflow-hidden">
                              <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Math.min((platformStats.avg_latency / 200) * 100, 100)}%` }}></div>
                            </div>
                          </div>
                          <div className="p-4 rounded-xl bg-gradient-to-r from-white/[0.02] to-transparent border border-white/5">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs text-gray-400 font-bold">P95 Latency</span>
                              <span className={`text-xs font-bold ${platformStats.p95_latency < 200 ? 'text-green-400' : 'text-red-400'}`}>{platformStats.p95_latency}ms</span>
                            </div>
                            <div className="w-full bg-[#0F0F1A] h-1.5 rounded-full overflow-hidden">
                              <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.min((platformStats.p95_latency / 1000) * 100, 100)}%` }}></div>
                            </div>
                          </div>
                          <div className="p-4 rounded-xl bg-gradient-to-r from-white/[0.02] to-transparent border border-white/5">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs text-gray-400 font-bold">Storage Used</span>
                              <span className="text-xs font-bold text-white">{platformStats.total_storage_gb} GB</span>
                            </div>
                            <div className="w-full bg-[#0F0F1A] h-1.5 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(platformStats.total_storage_gb / 100) * 100}%` }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {view === "storage" && (
                  <div className="bg-[#1A1A2E] rounded-2xl border border-white/5 shadow-xl overflow-hidden animate-slide-up">
                    <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                      <div>
                        <h2 className="text-lg font-bold text-white">Detailed Storage Analysis</h2>
                        <p className="text-xs text-gray-500 mt-1 font-bold">Breakdown of storage usage per bot</p>
                      </div>
                      <button onClick={() => setView("overview")} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all font-bold">
                        <FaArrowUp className="rotate-45" /> Back to Overview
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-black/20 text-gray-400 text-xs uppercase tracking-wider font-bold">
                          <tr>
                            <th className="px-6 py-4">Bot ID</th>
                            <th className="px-6 py-4">Tenant ID</th>
                            <th className="px-6 py-4 text-right">Total (MB)</th>
                            <th className="px-6 py-4">Breakdown (MB)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {storageLoading ? (
                            <tr>
                              <td colSpan="4" className="px-6 py-10 text-center">
                                <div className="flex flex-col items-center gap-3 text-gray-400">
                                  <FaSpinner className="animate-spin text-2xl text-cyan-400" />
                                  <span className="text-sm font-bold">Fetching storage data...</span>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <>
                              {storageData.active.length > 0 && (
                                <tr>
                                  <td colSpan="4" className="px-6 py-3 text-xs text-green-400 font-bold uppercase">Active</td>
                                </tr>
                              )}
                              {storageData.active.map((item, idx) => (
                                <tr key={`active-${idx}`} className="hover:bg-white/[0.02] transition-colors group">
                                  <td className="px-6 py-4 font-bold text-white flex items-center gap-3">
                                    <div className="w-8 h-8 rounded bg-green-500/10 flex items-center justify-center text-green-400">
                                      <FaHdd />
                                    </div>
                                    {item.bot_id}
                                  </td>
                                  <td className="px-6 py-4 font-mono text-xs text-gray-500 font-bold">{item.tenant_id}</td>
                                  <td className="px-6 py-4 text-right font-bold text-cyan-400">{item.total_mb} MB</td>
                                  <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(item.breakdown).map(([key, val]) => (
                                        <span key={key} className="text-[10px] font-bold bg-white/5 px-2 py-1 rounded border border-white/5 text-gray-300">{key}: {val} MB</span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {storageData.inactive.length > 0 && (
                                <tr>
                                  <td colSpan="4" className="px-6 py-3 text-xs text-red-400 font-bold uppercase">Inactive</td>
                                </tr>
                              )}
                              {storageData.inactive.map((item, idx) => (
                                <tr key={`inactive-${idx}`} className="opacity-60">
                                  <td className="px-6 py-4 font-bold text-white">{item.bot_id}</td>
                                  <td className="px-6 py-4 text-gray-500">{item.tenant_id}</td>
                                  <td className="px-6 py-4 text-right text-gray-400">{item.total_mb} MB</td>
                                  <td className="px-6 py-4">...</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {view === "tenants" && (
                  <div className="bg-[#1A1A2E] rounded-2xl border border-white/5 shadow-xl overflow-hidden animate-slide-up">
                    <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                      <div>
                        <h2 className="text-lg font-bold text-white">Tenant Directory</h2>
                        <p className="text-xs text-gray-500 mt-1 font-bold">Managing {tenants.length} {tenantFilter} tenants</p>
                      </div>
                      <button onClick={() => setView("overview")} className="text-xs text-cyan-400 hover:text-cyan-300">Back to Overview</button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-gradient-to-r from-[#141427] to-[#0F0F1A] text-gray-400 text-xs uppercase tracking-wider font-bold border-b border-white/10">
                          <tr>
                            <th className="px-6 py-4 text-left">Company Name</th>
                            <th className="px-6 py-4 text-left">Tenant ID</th>
                            <th className="px-6 py-4 text-left">Status</th>
                            <th className="px-6 py-4 text-left">Created At</th>
                            <th className="px-6 py-4 text-left">Features</th>
                            <th className="px-6 py-4 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {[...tenants]
                            .sort((a, b) => {
                              if (a.subscription_status === b.subscription_status) return 0;
                              return a.subscription_status === "active" ? -1 : 1;
                            })
                            .map(t => {
                            if (!tenantFeaturesMap[t.tenant_id]) {
                              return (
                                <tr key={t.tenant_id} className="hover:bg-white/[0.02] transition-colors">
                                  <td className="px-6 py-4">
                                    <span className="font-bold text-white">{t.company_name}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs text-gray-400">{t.tenant_id}</span>
                                      <button
                                        onClick={() => handleCopyTenantId(t.tenant_id)}
                                        className="p-1.5 rounded hover:bg-white/10 transition-colors text-gray-500 hover:text-cyan-400"
                                        title="Copy Tenant ID"
                                      >
                                        {copiedTenantId === t.tenant_id ? (
                                          <FaCheck className="text-green-400 text-xs" />
                                        ) : (
                                          <FaCopy className="text-xs" />
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${t.subscription_status === 'active' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                      {t.subscription_status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-gray-400">{new Date(t.created_at_utc).toLocaleDateString()}</td>
                                  <td className="px-6 py-4 text-xs text-gray-500">Loading...</td>
                                  <td className="px-6 py-4 text-right">-</td>
                                </tr>
                              );
                            }
                            const features = tenantFeaturesMap[t.tenant_id];
                            return (
                              <tr key={t.tenant_id} className="hover:bg-white/[0.02] transition-colors group">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/20">
                                      <FaBuilding className="text-cyan-400 text-xs" />
                                    </div>
                                    <span className="font-bold text-white">{t.company_name}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-gray-400 bg-white/5 px-2 py-1 rounded border border-white/5">{t.tenant_id}</span>
                                    <button
                                      onClick={() => handleCopyTenantId(t.tenant_id)}
                                      className="p-1.5 rounded hover:bg-white/10 transition-colors text-gray-500 hover:text-cyan-400"
                                      title="Copy Tenant ID"
                                    >
                                      {copiedTenantId === t.tenant_id ? (
                                        <FaCheck className="text-green-400 text-xs" />
                                      ) : (
                                        <FaCopy className="text-xs" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${t.subscription_status === 'active' ? 'bg-green-500/10 text-green-400 border border-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                    {t.subscription_status}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-400 text-xs">{new Date(t.created_at_utc).toLocaleDateString()}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-wrap gap-1.5 max-w-[280px]">
                                    {Object.entries(features).map(([key, value]) => (
                                      <label key={key} className="flex items-center gap-1.5 text-[10px] cursor-pointer group/checkbox">
                                        <input
                                          type="checkbox"
                                          checked={value}
                                          onChange={() => {
                                            const updated = { ...tenantFeaturesMap[t.tenant_id], [key]: !value };
                                            setTenantFeaturesMap(prev => ({ ...prev, [t.tenant_id]: updated }));
                                          }}
                                          className="w-3.5 h-3.5 rounded border-gray-600 bg-[#0F0F1A] text-cyan-500 focus:ring-cyan-500/20 cursor-pointer accent-cyan-500"
                                        />
                                        <span className="text-gray-400 font-mono group-hover/checkbox:text-gray-300 transition-colors">{key}</span>
                                      </label>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <button
                                    onClick={() => handleSaveFeatures(t.tenant_id)}
                                    disabled={savedTenantId === t.tenant_id}
                                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 justify-end w-full ${savedTenantId === t.tenant_id ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20'}`}
                                  >
                                    {savedTenantId === t.tenant_id ? (
                                      <><FaCheck className="text-xs" />Saved</>
                                    ) : (
                                      'Save'
                                    )}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {view === "bots" && (
                  <div className="space-y-6 animate-slide-up">
                    <div className="bg-[#1A1A2E] rounded-2xl border border-white/5 shadow-xl overflow-hidden">
                      <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                        <div>
                          <h2 className="text-lg font-bold text-white">Bot Registry</h2>
                          <p className="text-xs text-gray-500 mt-1 font-bold">Total deployed instances: {bots.length}</p>
                        </div>
                        <button onClick={() => setView("overview")} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all font-bold">
                          <FaArrowUp className="rotate-45" /> Back to Overview
                        </button>
                      </div>
                      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {Array.isArray(bots) && bots.map(b => {
                          const isSelected = selectedBotId === b.bot_id;
                          return (
                            <div
                              key={b.bot_id}
                              onClick={() => handleBotCardClick(b)}
                              className={`relative rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden flex flex-col ${isSelected ? "bg-[#141427] border-cyan-500/50 shadow-[0_0_30px_rgba(6,182,212,0.15)] col-span-1 lg:col-span-2 xl:col-span-3" : "bg-white/[0.02] border-white/5 hover:border-cyan-500/30 hover:bg-white/[0.03]"}`}
                            >
                              <div className="p-5 flex items-start justify-between">
                                <div className="flex items-center gap-4">
                                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-colors ${isSelected ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-400" : "bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-purple-500/20 text-purple-400"}`}>
                                    <FaRobot className="text-xl" />
                                  </div>
                                  <div>
                                    <h3 className="font-bold text-white text-lg truncate max-w-[200px]">{b.company_name}</h3>
                                    <p className="text-xs text-gray-500 font-mono font-bold">{b.bot_id}</p>
                                  </div>
                                </div>
                                <div className={`transform transition-transform duration-300 ${isSelected ? "rotate-180" : ""}`}>
                                  <FaChevronRight className="text-gray-600" />
                                </div>
                              </div>
                              {isSelected && (
                                <div className="border-t border-white/5 bg-black/20 p-6 animate-fade-in">
                                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-2">
                                        <FaChartLine /> Live Analytics
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 bg-[#0F0F1A] p-2 rounded-xl border border-white/5" onClick={(e) => e.stopPropagation()}>
                                      <select
                                        value={botTrendPeriod}
                                        onChange={(e) => { e.stopPropagation(); handleBotPeriodChange(e.target.value); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="bg-transparent border border-white/10 text-xs text-white rounded px-3 py-1.5 focus:outline-none focus:border-cyan-500/50 font-bold"
                                      >
                                        <option value="day">Daily</option>
                                        <option value="month">Monthly</option>
                                      </select>
                                      <div className="h-4 w-[1px] bg-white/10"></div>
                                      <input
                                        type="date"
                                        value={botDateStart}
                                        onChange={(e) => { e.stopPropagation(); handleBotDateChange('start', e.target.value); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="bg-transparent border border-white/10 text-[10px] text-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-cyan-500/50 font-mono"
                                      />
                                      <span className="text-gray-600 text-xs">-</span>
                                      <input
                                        type="date"
                                        value={botDateEnd}
                                        onChange={(e) => { e.stopPropagation(); handleBotDateChange('end', e.target.value); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="bg-transparent border border-white/10 text-[10px] text-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-cyan-500/50 font-mono"
                                      />
                                    </div>
                                  </div>
                                  <div className="h-[300px] w-full bg-[#0F0F1A] rounded-xl border border-white/5 p-4 relative" onClick={(e) => e.stopPropagation()}>
                                    {isTrendLoading ? (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <FaSpinner className="animate-spin text-2xl text-cyan-500" />
                                      </div>
                                    ) : botTrendData.length > 0 ? (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={botTrendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                          <defs>
                                            <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1">
                                              <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} />
                                              <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorMsg" x1="0" y1="0" x2="0" y2="1">
                                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                          </defs>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                                          <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={(val) => botTrendPeriod === 'month' ? val : val.slice(5)} />
                                          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                          <Tooltip content={<CustomTooltip />} />
                                          <Area type="monotone" dataKey="conversations" stroke="#22d3ee" strokeWidth={2} fillOpacity={1} fill="url(#colorConv)" name="Conversations" />
                                          <Area type="monotone" dataKey="messages" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorMsg)" name="Messages" />
                                        </AreaChart>
                                      </ResponsiveContainer>
                                    ) : (
                                      <div className="h-full flex items-center justify-center text-gray-500 text-sm font-bold">No trend data available for this range</div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {!isSelected && (
                                <div className="px-5 pb-5 pt-0">
                                  <div className="pt-3 border-t border-white/5 flex justify-between items-center text-xs">
                                    <span className="text-gray-600 font-bold">Deployed</span>
                                    <span className="text-gray-400 font-bold">{new Date(b.created_at_utc).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {view === "monthly" && (
                  <div className="bg-[#1A1A2E] rounded-2xl border border-white/5 shadow-xl overflow-hidden animate-slide-up">
                    <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                      <div>
                        <h2 className="text-lg font-bold text-white">Monthly Analytics</h2>
                        <p className="text-xs text-gray-500 mt-1 font-bold">Full historical breakdown</p>
                      </div>
                      <button onClick={() => setView("overview")} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all font-bold">Back</button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-black/20 text-gray-400 text-xs uppercase tracking-wider font-bold">
                          <tr>
                            <th className="px-6 py-4">Month</th>
                            <th className="px-6 py-4">Tenants</th>
                            <th className="px-6 py-4">Bots</th>
                            <th className="px-6 py-4">Users</th>
                            <th className="px-6 py-4">Conversations</th>
                            <th className="px-6 py-4">Messages</th>
                            <th className="px-6 py-4">Latency</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {monthlyData.map((m, i) => (
                            <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                              <td className="px-6 py-4 font-bold text-white">{m.month}</td>
                              <td className="px-6 py-4 text-gray-300">{m.tenants}</td>
                              <td className="px-6 py-4 text-gray-300">{m.bots}</td>
                              <td className="px-6 py-4 text-gray-300">{m.users}</td>
                              <td className="px-6 py-4 text-gray-300">{m.conversations}</td>
                              <td className="px-6 py-4 text-gray-300">{m.messages}</td>
                              <td className="px-6 py-4 text-gray-300">{Number(m.avg_latency || 0).toFixed(2)} ms</td>
                            </tr>
                          ))}
                          {monthlyData.length === 0 && (
                            <tr>
                              <td colSpan="7" className="px-6 py-8 text-center text-gray-500 font-bold">No monthly data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "admin" && (
          <div className="flex justify-center items-center min-h-[60vh]">
            <div className="w-full max-w-md p-8 bg-[#1A1A2E] border border-white/5 rounded-2xl shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500"></div>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-cyan-500/30 shadow-lg shadow-cyan-500/10">
                  <FaBuilding className="text-2xl text-cyan-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Access Tenant</h2>
                <p className="text-gray-500 text-sm mt-2 font-bold">Enter Tenant ID to view detailed analytics</p>
              </div>
              <form onSubmit={handleAccess} className="space-y-5">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">Tenant ID</label>
                  <input
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    className="w-full px-4 py-3 bg-[#0F0F1A] border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all text-sm text-white placeholder-gray-600 font-mono font-bold"
                    placeholder="tenant_xxxxx"
                    required
                    autoComplete="off"
                  />
                </div>
                {accessError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs flex items-center gap-2 font-bold">
                    <FaExclamationCircle className="text-xs" />
                    {accessError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isAccessing}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white py-3.5 rounded-xl font-bold transition-all text-sm flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAccessing ? (
                    <><FaSpinner className="animate-spin text-sm" /><span>Authenticating...</span></>
                  ) : (
                    <><span>View Tenant Dashboard</span><FaArrowUp className="rotate-45 text-xs" /></>
                  )}
                </button>
              </form>
              {tenantData && (
                <div className="mt-8 pt-6 border-t border-white/5 animate-fade-in">
                  <p className="text-sm text-cyan-400 font-bold mb-4 flex items-center gap-2 justify-center">
                    <FaCheckCircle className="text-xs" />
                    Access Granted: {tenantData.company_name}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                      <span className="text-[10px] text-gray-500 block mb-1 uppercase tracking-wider font-bold">Bots</span>
                      <span className="text-lg font-bold text-white">{tenantData.total_bots}</span>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                      <span className="text-[10px] text-gray-500 block mb-1 uppercase tracking-wider font-bold">Users</span>
                      <span className="text-lg font-bold text-white">{tenantData.total_users}</span>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                      <span className="text-[10px] text-gray-500 block mb-1 uppercase tracking-wider font-bold">Status</span>
                      <span className="text-sm font-bold text-green-400 capitalize">{tenantData.subscription_status}</span>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                      <span className="text-[10px] text-gray-500 block mb-1 uppercase tracking-wider font-bold">Latency</span>
                      <span className="text-sm font-bold text-white">{tenantData.average_response_time_ms}ms</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "referral" && (
          <div className="max-w-2xl mx-auto py-10">
            <div className="bg-gradient-to-br from-[#1A1A2E] to-[#141427] p-10 rounded-3xl border border-white/5 text-center relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500"></div>
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-cyan-500/30 shadow-lg shadow-cyan-500/10 relative z-10">
                <FaCode className="text-3xl text-cyan-400" />
              </div>
              <h2 className="text-3xl font-bold mb-3 text-white relative z-10">Referral Code</h2>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function NavItem({ label, icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 px-4 py-2 rounded-lg transition-all duration-200 text-xs font-bold tracking-wide ${active ? "bg-white/10 text-cyan-400 shadow-lg shadow-black/20" : "text-gray-500 hover:bg-white/5 hover:text-gray-300"}`}
    >
      <span className={`${active ? "text-cyan-400" : "text-gray-500 group-hover:text-gray-300"}`}>{icon}</span>
      {label}
    </button>
  );
}

function StatCard({ label, value, icon, trend, subText, onClick }) {
  const isUp = trend > 0;
  return (
    <div onClick={onClick} className="cursor-pointer p-5 bg-[#1A1A2E] rounded-2xl border border-white/5 hover:border-cyan-500/30 transition-all shadow-lg">
      <div className="flex justify-between items-start mb-2">
        <div className="p-2.5 rounded-xl text-cyan-400">{icon}</div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-bold ${isUp ? "text-green-400" : "text-red-400"}`}>
            {isUp ? <FaArrowUp /> : <FaArrowDown />}
            {Math.abs(trend).toFixed(0)}%
          </div>
        )}
      </div>
      <p className="text-gray-500 text-[10px] font-bold uppercase">{label}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subText && <p className="text-xs text-gray-400 mt-1">{subText}</p>}
    </div>
  );
}